import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MapPinned } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { LOGO_URL, BRAND } from "../data/mock";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/features", label: "Features" },
  { to: "/dashboard", label: "My Maps" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" }
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-40 transition-colors duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur border-b border-stone-200" : "bg-white border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <img
            src={LOGO_URL}
            alt={BRAND.name}
            className="h-9 w-9 object-contain rounded"
          />
          <div className="hidden sm:block leading-tight">
            <div className="font-bold text-[15px] text-stone-800 tracking-tight">{BRAND.name}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-semibold">{BRAND.tagline}</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "text-emerald-800 bg-emerald-50"
                    : "text-stone-700 hover:text-emerald-800 hover:bg-stone-50"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Button variant="ghost" className="text-stone-700 hover:text-emerald-800 hover:bg-stone-50">Sign in</Button>
          <Link to="/dashboard">
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2">
              <MapPinned className="h-4 w-4" /> Open Maps
            </Button>
          </Link>
        </div>

        <button
          className="md:hidden p-2 rounded-md text-stone-700 hover:bg-stone-100"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-stone-200 bg-white">
          <div className="px-5 py-3 flex flex-col gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-md text-sm font-medium ${
                    isActive ? "text-emerald-800 bg-emerald-50" : "text-stone-700 hover:bg-stone-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/dashboard" className="mt-2">
              <Button className="w-full bg-emerald-700 hover:bg-emerald-800 text-white gap-2">
                <MapPinned className="h-4 w-4" /> Open Maps
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
